<template>
  <v-container class="contact-page" fluid>
    <v-popup-loading :isLoading="isPageLoading" />
    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">{{ isEditMode ? 'Edit Rights Entry' : 'Create Rights Entry' }}</h1>
        <p class="subhead mb-0">Fields marked <span class="required">*</span> are required.</p>
      </div>
      <div class="top-actions">
        <v-btn outlined class="mr-2" @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save</v-btn>
      </div>
    </div>

    <v-card class="form-card">
      <div class="form-card-head">Classification</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="4">
            <label>RIGHTS TYPE <span class="required">*</span></label>
            <v-select dense outlined hide-details :items="rightsTypeOptions" item-text="text" item-value="value" v-model="form.rights_type"/>
          </v-col>
          <v-col v-if="isSuperAdmin" cols="12" md="4">
            <label>BRANCH <span class="required">*</span></label>
            <v-select dense outlined hide-details :items="orgOptions" item-text="text" item-value="value" v-model="form.org_codename" placeholder="Select Branch"/>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Content</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6"><label>TITLE <span class="lang">TH</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.title_th" placeholder="เช่น สิทธิเบิกจ่ายตรงข้าราชการ"/></v-col>
          <v-col cols="12" md="6"><label>TITLE <span class="lang">EN</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.title_en" placeholder="e.g. Civil Servant Direct Billing"/></v-col>
          <v-col cols="12" md="6"><label>DESCRIPTION <span class="lang">TH</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.description_th" placeholder="รายละเอียด..."/></v-col>
          <v-col cols="12" md="6"><label>DESCRIPTION <span class="lang">EN</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.description_en" placeholder="Description..."/></v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">
        <div>Conditions &amp; Terms</div>
        <div class="head-sub">Detailed eligibility rules and conditions.</div>
      </div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6">
            <label>CONDITIONS <span class="lang">TH</span></label>
            <quill-editor
              v-model="form.conditions_th"
              :options="conditionsEditorOptionTh"
              class="conditions-editor"
            />
          </v-col>
          <v-col cols="12" md="6">
            <label>CONDITIONS <span class="lang">EN</span></label>
            <quill-editor
              v-model="form.conditions_en"
              :options="conditionsEditorOptionEn"
              class="conditions-editor"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card actions-card">
      <v-card-actions class="modal-foot">
        <v-spacer />
        <v-btn outlined @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import {quillEditor} from 'vue-quill-editor'
import PopupLoading from '@/components/PopupLoading'

const defaultForm = () => ({
  id: null,
  org_codename: '',
  rights_type: 'government',
  title_th: '',
  title_en: '',
  description_th: '',
  description_en: '',
  conditions_th: '',
  conditions_en: ''
})

export default {
  components: {
    quillEditor,
    'v-popup-loading': PopupLoading
  },
  data() {
    return {
      form: defaultForm(),
      initialLoading: false,
      rightsTypeOptions: [
        { text: 'Government Officer', value: 'government' },
        { text: 'Academic & Educational', value: 'education' },
        { text: 'General Treatment Benefits', value: 'treatment' }
      ]
    }
  },
  computed: {
    ...mapState('specialRights', ['list', 'isSaving', 'orgOptions']),
    ...mapState('auth', ['user']),
    isPageLoading() { return this.initialLoading },
    isEditMode() { return !!this.$route.params.id },
    isSuperAdmin() {
      const currentUser = this.user || {}
      return !!currentUser.is_superuser
    },
    canSave() { return this.isEditMode ? hasPermision('CanEditSpecialRight') : hasPermision('CanAddSpecialRight') },
    conditionsEditorOptionTh() {
      return {
        placeholder: 'กรอกรายละเอียดเงื่อนไข...',
        modules: {
          toolbar: [['bold', 'italic', 'underline'], [{list: 'ordered'}, {list: 'bullet'}]]
        }
      }
    },
    conditionsEditorOptionEn() {
      return {
        placeholder: 'Type the eligibility rules and terms & conditions here...',
        modules: {
          toolbar: [['bold', 'italic', 'underline'], [{list: 'ordered'}, {list: 'bullet'}]]
        }
      }
    },
    isFormValid() {
      return !!(this.form.org_codename && this.form.rights_type && this.form.title_th && this.form.title_en)
    }
  },
  async created() {
    this.initialLoading = true
    try {
      this.setResource('special-rights')
      await this.fetchOrgOptions()
      await this.fetch()
      if (this.isEditMode) {
        const target = this.list.find(item => String(item.id) === String(this.$route.params.id))
        if (target) this.form = {...defaultForm(), ...target}
      } else {
        this.form.org_codename = this.getUserOrgCode() || (this.orgOptions[0] ? this.orgOptions[0].value : '')
      }
    } finally {
      this.initialLoading = false
    }
  },
  methods: {
    ...mapActions('specialRights', ['setResource', 'fetch', 'save', 'fetchOrgOptions']),
    getUserOrgCode() {
      const currentUser = this.user || {}
      return currentUser.org_code || currentUser.org_codename || currentUser.org || ''
    },
    onCancel() { this.$router.push({name: 'special-right-list'}) },
    async onSave() {
      const result = await this.$store.dispatch('specialRights/save', this.form)
      if (!result.ok) return
      this.$router.push({name: 'special-right-list'})
    }
  }
}
</script>

<style scoped>
.contact-page { background:#f7f9fc; min-height:calc(100vh - 120px); }
.head-wrap { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; }
.headline { font-size:30px!important; color:#0f1f3b; }
.subhead { color:#64748b; font-size:13px; }
.top-actions { display:flex; align-items:center; }
.form-card { border:1px solid #e6e9f0; border-radius:10px; margin-bottom:14px; background:#fff; overflow:hidden; }
.form-card-head { padding:12px 14px; border-bottom:1px solid #edf2fb; font-size:16px; font-weight:700; color:#0f1f3b; }
.head-sub { font-size:12px; color:#7586a8; font-weight:500; margin-top:2px; }
.modal-foot { border-top:1px solid #edf2fb; padding:10px 14px; }
.required { color:#c62828; }
.lang { color:#2962ff; }
label { display:block; font-size:10.5px; color:#64748b; text-transform:uppercase; font-weight:700; margin-bottom:5px; letter-spacing:.4px; }
.actions-card { margin-bottom:0; }
.conditions-editor::v-deep .ql-toolbar.ql-snow { border-color:#dce6f8; border-radius:8px 8px 0 0; }
.conditions-editor::v-deep .ql-container.ql-snow { border-color:#dce6f8; border-radius:0 0 8px 8px; min-height:140px; }
</style>
